import {
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
  CHECKOUT_ITEM_TYPE_CAR_HIRE,
  CHECKOUT_ITEM_TYPE_CRUISE,
  CHECKOUT_ITEM_TYPE_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_FLIGHT,
  CHECKOUT_ITEM_TYPE_GIFT_CARD,
  CHECKOUT_ITEM_TYPE_INSURANCE,
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION,
  CHECKOUT_ITEM_TYPE_TOUR_V1,
  CHECKOUT_ITEM_TYPE_TOUR_V2,
  CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_TRANSFER,
  CHECKOUT_ITEM_TYPE_VILLA,
  POST_PURCHASE_CHECKOUT_MODES,
} from 'constants/checkout'
import { RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { PRODUCT_ID_NO_PROTECTION } from 'constants/insurance'
import { hasRoomTypePackageUpgrade } from 'lib/tours/tourUtils'
import { NO_BOOKING_PROTECTION_QUOTE_ID } from 'constants/bookingProtection'
import { PAYMENT_CHECKOUT_TYPES } from 'constants/payment'

const accommodationItemTypes = new Set([
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_TOUR_V1,
  CHECKOUT_ITEM_TYPE_TOUR_V2,
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_CRUISE,
  CHECKOUT_ITEM_TYPE_VILLA,
])

export function isInsuranceItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_INSURANCE
}

export function isInsuranceProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceItem(item) && item.productId !== PRODUCT_ID_NO_PROTECTION
}

export function isNoProtectionInsuranceItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceItem(item) && item.productId === PRODUCT_ID_NO_PROTECTION
}

export function isNoProtectionInsuranceBookingProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceBookingProtectionItem(item) && item.productId === PRODUCT_ID_NO_PROTECTION
}

export function isAccommodationItem(item: App.Checkout.AnyItem): item is App.Checkout.AccommodationItem {
  return accommodationItemTypes.has(item.itemType)
}

export function isBundleAndSaveItem(item: App.Checkout.AnyItem): item is App.Checkout.BundleAndSaveItem {
  return item.itemType === 'bundleAndSave'
}

export function isLEHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.LEHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL
}

export function isInstantBookingLEHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.InstantBookingLEHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL && item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING &&
    !!item.checkIn && !!item.checkOut
}

export function isInstantLEHotelPostPurchaseItem(item: App.Checkout.AnyItem): item is App.Checkout.InstantBookingLEHotelPostPurchaseItem {
  return isInstantBookingLEHotelItem(item) && !!item.orderId && !!item.orderItemId
}

export function isBNBLLEHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.BNBLLEHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL && item.reservationType === RESERVATION_TYPE_BOOK_LATER
}

export function isBNBLHotelSetDatesItem(item: App.Checkout.AnyItem): item is App.Checkout.BNBLLEHotelSetDatesItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL && item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING &&
    !!item.orderId && !!item.orderItemId
}

export function isInstantBookingHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.InstantBookingItem {
  return !isBNBLLEHotelItem(item)
}

export function isBedbankItem(item: App.Checkout.AnyItem): item is App.Checkout.BedbankHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_BEDBANK
}

export function isTourV1Item(item: App.Checkout.AnyItem): item is App.Checkout.LETourV1Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1
}

export function isTourV2Item(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2
}

export function isTourV2ExperienceItem(item: App.Checkout.AnyItem): item is App.Checkout.TourV2ExperienceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE
}

export function isTourItem(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item | App.Checkout.LETourV1Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2 || item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1
}

export function isTourV2ItemWithPackageUpgrade(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2 && (!item.purchasableOption.roomType || hasRoomTypePackageUpgrade(item.purchasableOption.roomType))
}

export function isTourV2ItemWithoutRoom(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2 && !item.purchasableOption.roomType
}

export function isCruiseItem(item: App.Checkout.AnyItem): item is App.Checkout.CruiseItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_CRUISE
}

export function isCarHireItem(item: App.Checkout.AnyItem): item is App.Checkout.CarHireItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_CAR_HIRE
}

export function isLEAccommodationItem(item: App.Checkout.AnyItem): item is App.Checkout.LEHotelItem | App.Checkout.LETourV1Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL || item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1
}

export function isExperienceItem(item: App.Checkout.AnyItem): item is App.Checkout.ExperienceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_EXPERIENCE
}

export function isTransferItem(item: App.Checkout.AnyItem): item is App.Checkout.TransferItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_TRANSFER
}

export function isFlightItem(item: App.Checkout.AnyItem): item is App.Checkout.FlightItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_FLIGHT
}

export function isGiftCardItem(item: App.Checkout.AnyItem): item is App.Checkout.GiftCardItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_GIFT_CARD
}

export function isLuxPlusSubscriptionItem(item: App.Checkout.AnyItem): item is App.Checkout.LuxPlusSubscriptionItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION
}

export function isSubscriptionJoinItem(item: App.Checkout.AnyItem): item is App.Checkout.SubscriptionJoinItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE
}

export function getConnectRoomsSpecialRequestMessage(contactName: string): string {
  return `All rooms under the name ${contactName}, are requested to be connecting rooms or within close proximity.`
}

export function getTourV2IdForCheckoutItem(item: App.Checkout.TourV2Item): string {
  return item.privateRequestKey ? `${item.offerId}-${item.privateRequestKey}` : item.offerId
}

export function isVillaItem(item: App.Checkout.AnyItem): item is App.Checkout.VillaItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_VILLA
}

export function isBookingProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.BookingProtectionItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION
}

export function isInsuranceBookingProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_INSURANCE && item.insuranceType === 'cfmr'
}

export function isBookingProtectionWithProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.BookingProtectionItem {
  return isBookingProtectionItem(item) && item.quoteId !== NO_BOOKING_PROTECTION_QUOTE_ID
}

export function isCruiseV1(item: App.Checkout.AnyItem, leOffers: Record<string, App.Offer | undefined>): boolean {
  if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1) {
    const offer = leOffers[item.offerId]
    return !!offer && !!offer.holidayTypes?.includes('Cruises')
  }
  return true
}

export function getItemOfferId(item?: App.Checkout.AccommodationItem) {
  if (!!item && isCruiseItem(item)) return `cruise-details:${item.offerId}`
  return item?.offerId
}

export function isBedbankAccommodationItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.BedbankAccommodationItemView {
  return isBedbankItem(itemView.item)
}

export function isLEHotelAccommodationItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.LEAccommodationItemView {
  return isLEHotelItem(itemView.item)
}

export function isInsuranceItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.InsuranceItemView {
  return isInsuranceItem(itemView.item)
}

export function findPostPurchaseCheckout(mode?: App.CheckoutCartMode) {
  if (mode && POST_PURCHASE_CHECKOUT_MODES.has(mode)) return mode
}

export function isLoyaltyLoungePassItem(item?: App.Checkout.AnyItem): item is App.Checkout.LoyaltyLoungePassItem {
  return item?.itemType === 'loungePassItem'
}

export function isLoyaltyTransferItem(item?: App.Checkout.AnyItem): item is App.Checkout.LoyaltyTransferItem {
  return item?.itemType === 'loyaltyTransferItem'
}

export function isExperienceTicketViewItem(item?: App.Checkout.AnyItem): item is App.Checkout.ExperienceTicketViewItem {
  return item?.itemType === 'experienceTicket'
}

export function mapCartModeToCheckoutType(cartMode: App.CheckoutCartMode | undefined): App.PaymentCheckoutType {
  if (!cartMode) {
    return PAYMENT_CHECKOUT_TYPES.Unknown
  }
  switch (cartMode) {
    case 'change-dates':
      return PAYMENT_CHECKOUT_TYPES.ChangeDates
    case 'change-package':
      return PAYMENT_CHECKOUT_TYPES.ChangePackage
    case 'add-extra-guests':
      return PAYMENT_CHECKOUT_TYPES.AddExtraGuests
    case 'custom-offer':
      return PAYMENT_CHECKOUT_TYPES.CustomOffer
    case 'instalment':
      return PAYMENT_CHECKOUT_TYPES.Instalment
    case 'instalment-balance':
      return PAYMENT_CHECKOUT_TYPES.InstalmentBalance
    case 'deposit-balance':
      return PAYMENT_CHECKOUT_TYPES.DepositBalance
    case 'reserve-balance':
      return PAYMENT_CHECKOUT_TYPES.ReserveBalance
    case 'payment-schedule-balance':
      return PAYMENT_CHECKOUT_TYPES.PaymentScheduleBalance
    case 'default':
      return PAYMENT_CHECKOUT_TYPES.Default
    default:
      return PAYMENT_CHECKOUT_TYPES.Unknown
  }
}

/**
 * Gets a display name for a traveller form.
 * Returns the full name if both first and last names are available,
 * otherwise returns the first name, last name, or a default fallback.
 *
 * @param form - The traveller form containing name data
 * @param fallback - The fallback text to display if no name is available (default: 'Guest traveller')
 * @returns A formatted display name string
 */
export function getTravellerDisplayName(
  form: App.Checkout.TravellerForm,
  fallback: string = 'Guest traveller',
): string {
  if (form.data.firstName && form.data.lastName) {
    return `${form.data.firstName} ${form.data.lastName}`
  }
  if (form.data.firstName) {
    return form.data.firstName
  }
  if (form.data.lastName) {
    return form.data.lastName
  }
  return fallback
}

/**
 * Gets the full name of a traveller from their form data.
 *
 * @param traveller - The traveller form
 * @returns The full name as "firstName lastName"
 */
export function getTravellerFullName(traveller: App.Checkout.TravellerForm): string {
  return `${traveller.data.firstName} ${traveller.data.lastName}`
}

/**
 * Gets the contact name, preferring the primary contact's name over the account name.
 *
 * @param primaryContact - The primary contact traveller form (optional)
 * @param account - The auth account to fall back to
 * @returns The contact name
 */
export function getContactName(primaryContact: App.Checkout.TravellerForm | undefined, account: App.AuthAccount): string {
  if (primaryContact?.data.firstName && primaryContact?.data.lastName) {
    return getTravellerFullName(primaryContact)
  }
  return [account.givenName, account.surname].filter(Boolean).join(' ')
}

/**
 * Finds a traveller form by their employeeId or guestId.
 *
 * @param travellerId - The employee ID or guest ID to search for
 * @param travellerForms - Array of traveller forms to search through
 * @returns The matching traveller form, or undefined if not found
 */
export function findTravellerById(
  travellerId: string,
  travellerForms: Array<App.Checkout.TravellerForm>,
): App.Checkout.TravellerForm | undefined {
  return travellerForms.find(
    form => form.employeeId === travellerId || form.guestId === travellerId
  )
}

/**
 * Gets the primary traveller for a room based on occupancy data.
 * The primary traveller is the first person in the employeeIds or guestIds array.
 *
 * @param occupancy - The room occupancy data containing employeeIds and guestIds
 * @param travellerForms - Array of traveller forms to search through
 * @returns The primary traveller form, or undefined if not found
 */
export function getPrimaryRoomTraveller(
  occupancy: App.Occupants | App.RoomOccupants | undefined,
  travellerForms: Array<App.Checkout.TravellerForm>,
): App.Checkout.TravellerForm | undefined {
  if (!occupancy) return undefined

  const roomTravellerIds = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ]

  return roomTravellerIds.length > 0
    ? findTravellerById(roomTravellerIds[0], travellerForms)
    : undefined
}

/**
 * Gets the hotel loyalty program member ID from room travellers.
 * Prioritises the primary room traveller's ID, falls back to additional travellers.
 *
 * @param occupancy - The room occupancy data containing employeeIds and guestIds
 * @param travellerForms - Array of traveller forms to search through
 * @param primaryTravellerId - The ID of the primary traveller to prioritise
 * @returns The hotel loyalty program member ID, or undefined
 */
export function getRoomLoyaltyProgramMemberId(
  occupancy: App.Occupants | App.RoomOccupants,
  travellerForms: Array<App.Checkout.TravellerForm>,
  primaryTravellerId?: string,
): string | undefined {
  // Check primary traveller first
  if (primaryTravellerId) {
    const primaryTraveller = findTravellerById(primaryTravellerId, travellerForms)
    if (primaryTraveller?.data.hotelLoyaltyProgramMemberId) {
      return primaryTraveller.data.hotelLoyaltyProgramMemberId
    }
  }

  // Fallback to additional travellers
  const additionalTravellerIds = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ].filter(id => id !== primaryTravellerId)

  for (const travellerId of additionalTravellerIds) {
    const traveller = findTravellerById(travellerId, travellerForms)
    if (traveller?.data.hotelLoyaltyProgramMemberId) {
      return traveller.data.hotelLoyaltyProgramMemberId
    }
  }

  return undefined
}

/**
 * Gets the names of additional travellers in a room (excluding the primary traveller).
 *
 * @param occupancy - The room occupancy data containing employeeIds and guestIds
 * @param travellerForms - Array of traveller forms to search through
 * @param primaryTravellerId - The ID of the primary traveller to exclude (optional)
 * @returns Array of full names of additional travellers
 */
export function getAdditionalTravellerNames(
  occupancy: App.Occupants | App.RoomOccupants,
  travellerForms: Array<App.Checkout.TravellerForm>,
  primaryTravellerId?: string,
): Array<string> {
  const additionalTravellerIds = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ].filter(id => id !== primaryTravellerId)

  return additionalTravellerIds
    .map(travellerId => findTravellerById(travellerId, travellerForms))
    .filter((traveller): traveller is App.Checkout.TravellerForm =>
      traveller !== undefined &&
      !!traveller.data.firstName &&
      !!traveller.data.lastName
    )
    .map(getTravellerFullName)
}
